import React, { useEffect, useMemo, useState, useContext } from 'react';
import {
  Avatar,
  AvatarGroup,
  Button,
  Clickable,
  DataGrid,
  DataGridProps,
  DataGridUtils,
  getDateTime,
  Icon,
  Lightbox,
  P,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import { capitalize } from 'lodash';
import { CounterDrawer } from './drawer/CounterDrawer';
import { useCounterContext } from './hooks/useCounter';
import { CounterRow, vendorColumns, baselineCostColumns } from './columns';
import CounterShippedDialog from './dialogs/CounterShippedDialog';
import PrevSampleReceivedDialog from './dialogs/PrevSampleReceivedDialog';
import { CounterDialogState } from './drawer/CounterInformation';
import {
  Counter,
  CounterQuoteStatus,
  counterQuoteStatusColorMapping,
  counterQuoteStatusDisplayMapping,
  CounterQuoteStatusToHide,
  counterSampleStatusColorMapping,
  counterSampleStatusDisplayMapping,
  prevSampleStatusColorMapping,
  prevSampleStatusDisplayMapping,
} from './types';
import { CounterLatestMessage } from './LastMessage';
import CounterContactDialog from './dialogs/CounterContactDialog';
import countries from '../../Lib/Constants/countries';
import { BASELINE_COST_TOOLTIP } from '../../Lib/Constants/ui-text';
import { ChatUserTooltip } from '../Chat/ChatUserTooltip';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';

type Props = {
  data: Counter[];
  isLoading: boolean;
  vendorId: string;
  userId: number;
  counterIdParam: string | null;
} & Omit<DataGridProps<CounterRow>, 'columns'>;

const priorityBadgeColorMapping = {
  PreseasonQ3Q4: 'yellow',
  Reorders: 'primary',
  PreseasonQ1Q2: 'neutral',
  Immediate: 'red',
} as const;

function CounterDatagrid({
  data,
  isLoading,
  vendorId,
  userId,
  counterIdParam,
  ...others
}: Props) {
  const { flags } = useContext(FeatureFlagsContext);
  const [selectedPhotos, setSelectedPhotos] = useState<{ src: string }[]>([]);
  const [isLightboxOpened, setIsLightboxOpened] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<{
    opened: boolean;
    tab: 'chat' | 'quote';
  }>({
    opened: false,
    tab: 'chat',
  });
  const apiRef = DataGridUtils.useGridApiRef();
  const {
    selectedCounter,
    setSelectedCounter,
    setAllCounters,
    setVendorId,
    setUserId,
  } = useCounterContext();
  const [dialogState, setDialogState] = useState<CounterDialogState>({
    counterShipped: false,
    prevSampleReceived: false,
    counterContacts: false,
  });
  const closeDialog = (dialogKey: keyof CounterDialogState) => {
    setDialogState((prev) => ({
      ...prev,
      [dialogKey]: false,
    }));
  };

  const openDialog = (dialogKey: keyof CounterDialogState) => {
    setDialogState((prev) => ({
      ...prev,
      [dialogKey]: true,
    }));
  };

  useEffect(() => {
    if (selectedCounter && apiRef?.current?.setRowSelectionModel) {
      apiRef.current.setRowSelectionModel([selectedCounter.counter_id]);
    }
  }, [selectedCounter, apiRef]);

  useEffect(() => {
    if (counterIdParam && data) {
      const selectedRow = data.find((row) => row.counter_id === counterIdParam);
      if (selectedRow) {
        setSelectedCounter(selectedRow);
        setIsDrawerOpen({ opened: true, tab: 'chat' });
      }
    }
  }, [counterIdParam, data]);

  const sanitizeAndExtractText = (htmlContent: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const listElements = Array.from(
      doc.body.getElementsByTagName('li'),
      (element) => element.textContent,
    );

    const spanElements = Array.from(
      doc.body.getElementsByTagName('span'),
      (element) => element.textContent,
    );

    const pElements = Array.from(
      doc.body.getElementsByTagName('p'),
      (element) => element.textContent,
    );

    return Array.from(
      new Set([...listElements, ...spanElements, ...pElements]),
    );
  };

  const transformedRows = useMemo(
    () =>
      data?.map((row: Counter) => {
        const quoteStatusToHide = Object.values(
          CounterQuoteStatusToHide,
        ) as string[];

        const shouldHideQuoteStatus = quoteStatusToHide.includes(
          row?.counter_quote_status as string,
        );

        const quoteStatus = shouldHideQuoteStatus
          ? row?.prev_counter_quote_status
          : (row?.counter_quote_status as CounterQuoteStatus);

        let counterQuoteStatusMapped =
          counterQuoteStatusDisplayMapping[
            quoteStatus as keyof typeof counterQuoteStatusDisplayMapping
          ] || row.counter_quote_status;

        /**
         * If the quote status is in the hide list (final review or discuss), we need to get the status from the
         * counter_quote property, since we are not sure if the quote was already sent
         */
        if (shouldHideQuoteStatus) {
          counterQuoteStatusMapped = row?.counter_quote
            ? 'Quote Sent'
            : 'Quote Not Sent';
        }

        const prevSampleStatusMapped =
          prevSampleStatusDisplayMapping[
            row.prev_sample_status as keyof typeof prevSampleStatusDisplayMapping
          ] || row.prev_sample_status;
        const counterSampleStatusMapped =
          counterSampleStatusDisplayMapping[
            row.counter_sample_status as keyof typeof counterSampleStatusDisplayMapping
          ] || row.counter_sample_status;
        const isCounterQuoteNotApproved =
          counterQuoteStatusMapped !== 'Quote Approved';

        const sanitizedDescription = sanitizeAndExtractText(
          row.product_description || '',
        );

        const rowData: any = {
          id: row.counter_id,
          photo: {
            alt: 'photo',
            photo: row.photos?.[0],
            onClick: () => {
              setSelectedPhotos(
                row.photos.map((photo: string) => ({
                  src: photo,
                })),
              );
              setIsLightboxOpened(true);
            },
          },
          'last_message.updated_at': (
            <Clickable
              className="h-full flex w-full"
              onClick={() => {
                setSelectedCounter(row);
                setIsDrawerOpen({ opened: true, tab: 'chat' });
              }}
            >
              <CounterLatestMessage counterId={row.counter_id} />
            </Clickable>
          ),
          priority: {
            id: 'badge_priority',
            children: row.priority,
            contrast: 'low',
            color:
              priorityBadgeColorMapping[
                row.priority as keyof typeof priorityBadgeColorMapping
              ] ?? 'neutral',
          },
          vendor_contacts: (
            <Clickable
              className="w-full"
              onClick={() => {
                setSelectedCounter(row);
                openDialog('counterContacts');
              }}
            >
              <AvatarGroup max={3}>
                {row.vendor_contacts?.map((contact) => (
                  <ChatUserTooltip
                    key={contact.id}
                    id={contact.id}
                    name={contact.full_name}
                    userType="vp"
                  >
                    <Avatar name={contact.full_name} badgeType="vendor" />
                  </ChatUserTooltip>
                ))}
              </AvatarGroup>
              {!row.vendor_contacts?.length && (
                <P size="sm" color="disabled">
                  -
                </P>
              )}
            </Clickable>
          ),
          fn_contacts: (
            <Clickable
              className="w-full"
              onClick={() => {
                setSelectedCounter(row);
                openDialog('counterContacts');
              }}
            >
              <AvatarGroup max={3}>
                {row.fn_contacts?.map((contact) => (
                  <ChatUserTooltip
                    key={contact.id}
                    id={contact.id}
                    name={contact.full_name}
                    userType="internal"
                  >
                    <Avatar name={contact.full_name} badgeType="fn_internal" />
                  </ChatUserTooltip>
                ))}
              </AvatarGroup>
              {!row.fn_contacts?.length && (
                <P size="sm" color="disabled">
                  -
                </P>
              )}
            </Clickable>
          ),
          style_color: row.style_color,
          division: row.division,
          product_description: (
            <ul className="leading-4">
              {sanitizedDescription?.map((item) =>
                item ? (
                  <li
                    className="text-gray-600 whitespace-normal text-left overflow-hidden"
                    key={item}
                  >
                    <span className="inline-block h-1 text-base mr-1">
                      &#8226;
                    </span>
                    <span>{item}</span>
                  </li>
                ) : null,
              )}
            </ul>
          ),
          display_name: row.display_name,
          prev_fabric_content: row.counter_quote?.prev_fabric_content,
          prev_fabric_weight: row.counter_quote?.prev_fabric_weight,
          prev_fabric_name: row.counter_quote?.prev_fabric_name,
          prev_fabric_type: row.counter_quote?.prev_fabric_type,
          request_counter_quote_to_vendor: {
            date: row.request_counter_quote_to_vendor,
            activity: false,
            time: false,
          },
          prev_sample_status: {
            id: 'badge_prev_sample_status',
            children: prevSampleStatusMapped,
            contrast: 'low',
            color:
              prevSampleStatusColorMapping[
                prevSampleStatusMapped as keyof typeof prevSampleStatusColorMapping
              ] ?? 'neutral',
          },
          prev_sample_shipped_at: {
            date: row.prev_sample_shipped_at,
            activity: false,
            time: false,
            timeZone: 'UTC',
          },
          prev_sample_tracking_to_vendor:
            row?.prev_sample_tracking_to_vendor && (
              <Clickable
                onClick={() =>
                  row?.prev_sample_tracking_to_vendor &&
                  window.open(row.prev_sample_tracking_to_vendor, '_blank')
                }
              >
                <P size="xsm" weight="normal" color="purple">
                  View Tracking
                </P>
              </Clickable>
            ),
          prev_sample_vendor_received_at: (
            <div>
              {prevSampleStatusMapped ===
                prevSampleStatusDisplayMapping.ShippedToVendor &&
              isCounterQuoteNotApproved ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedCounter(row);
                    openDialog('prevSampleReceived');
                  }}
                  className="w-full"
                  color="secondary"
                >
                  Mark As Received
                </Button>
              ) : (
                row.prev_sample_vendor_received_at &&
                getDateTime(row.prev_sample_vendor_received_at, false, 'UTC')
              )}
            </div>
          ),
          counter_sample_status: {
            id: 'badge_counter_sample_status',
            children: counterSampleStatusMapped,
            contrast: 'low',
            color:
              counterSampleStatusColorMapping[
                counterSampleStatusMapped as keyof typeof counterSampleStatusColorMapping
              ] ?? 'neutral',
          },
          counter_sample_shipped: (
            <div>
              {counterSampleStatusMapped ===
                counterSampleStatusDisplayMapping.Pending &&
              isCounterQuoteNotApproved ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedCounter(row);
                    openDialog('counterShipped');
                  }}
                  className="w-full"
                  color="secondary"
                >
                  Mark As Shipped
                </Button>
              ) : (
                row.counter_sample_shipped_at &&
                getDateTime(row.counter_sample_shipped_at, false, 'UTC')
              )}
            </div>
          ),
          counter_sample_tracking: row.counter_sample_tracking && (
            <Clickable
              onClick={() => window.open(row.counter_sample_tracking, '_blank')}
            >
              <P size="xsm" weight="normal" color="purple">
                View Tracking
              </P>
            </Clickable>
          ),
          counter_sample_received_at: (
            <div>
              {row.counter_sample_received_at &&
                getDateTime(row.counter_sample_received_at, false, 'UTC')}
            </div>
          ),
          counter_quote_status: {
            id: 'badge_counter_quote_status',
            children: counterQuoteStatusMapped,
            contrast: 'low',
            color:
              counterQuoteStatusColorMapping[
                counterQuoteStatusMapped as keyof typeof counterQuoteStatusColorMapping
              ] ?? 'default',
          },
          'counter_quote.ranges.jr_cost': (() => {
            const ranges = row.counter_quote?.ranges;
            if (!ranges?.length) return '-';
            const costs = ranges.map((r: { jr_cost: number }) => r.jr_cost);
            const min = Math.min(...costs);
            const max = Math.max(...costs);
            return min === max ? `$${min}` : `$${min} - $${max}`;
          })(),
          'counter_quote.ranges.plus_cost': (() => {
            const ranges = row.counter_quote?.ranges;
            if (!ranges?.length) return '-';
            const costs = ranges
              .filter((r) => r.plus_cost !== null)
              .map((r) => r.plus_cost as number);
            if (!costs.length) return '-';
            const min = Math.min(...costs);
            const max = Math.max(...costs);
            return min === max ? `$${min}` : `$${min} - $${max}`;
          })(),
        };

        if (flags?.baseline_cost?.enabled) {
          // Baseline costs are product-vendor-specific attributes, not range-level
          rowData['counter_quote.ranges.baseline_jr_cost'] =
            row.baseline_jr_cost != null ? `$${row.baseline_jr_cost}` : '-';
          rowData['counter_quote.ranges.baseline_plus_cost'] =
            row.baseline_plus_cost != null ? `$${row.baseline_plus_cost}` : '-';
        }

        rowData['counter_quote.moq'] = row.counter_quote?.moq ?? '-';
        rowData['counter_quote.mcq'] = row.counter_quote?.mcq ?? '-';
        rowData.coo = row?.counter_quote?.coo
          ? countries.find((c) => c.value === row.counter_quote?.coo)?.text
          : '-';
        rowData.shipment_mode = row.counter_quote?.shipment_mode
          ? capitalize(row.counter_quote.shipment_mode)
          : '-';
        rowData['counter_quote.fabric_content'] =
          row.counter_quote?.fabric_content ?? '-';
        rowData.fabric_weight = row.counter_quote?.fabric_weight ?? '-';
        rowData.fabric_name = row.counter_quote?.fabric_code_name ?? '-';
        rowData.fabric_type = row.counter_quote?.fabric_type ?? '-';
        rowData['counter_quote.lead_time_in_weeks'] = row.counter_quote
          ?.lead_time_in_weeks
          ? `${row.counter_quote?.lead_time_in_weeks} weeks`
          : '-';
        rowData['counter_quote.ro_lead_time_in_weeks'] = row.counter_quote
          ?.ro_lead_time_in_weeks
          ? `${row.counter_quote?.ro_lead_time_in_weeks} weeks`
          : '-';
        rowData.actions = ['Quote Sent', 'Quote Not Sent'].includes(
          counterQuoteStatusMapped,
        ) ? (
          <Button
            onClick={() => {
              setSelectedCounter(row);
              setIsDrawerOpen({ opened: true, tab: 'quote' });
            }}
            color={
              counterQuoteStatusMapped === 'Quote Not Sent'
                ? 'primary'
                : 'secondary'
            }
          >
            {counterQuoteStatusMapped === 'Quote Not Sent' ? (
              <>Submit Quote</>
            ) : (
              <>
                <Icon name="pencil-edit/pencil-edit-line" />
                Edit Quote
              </>
            )}
          </Button>
        ) : (
          ''
        );

        return rowData;
      }),
    [data, setSelectedCounter, flags?.baseline_cost?.enabled],
  );

  // Prepare the column headers with tooltips for baseline costs if enabled
  const columns = useMemo(() => {
    const cols = [...vendorColumns];

    // Find the index where we want to insert baseline columns (after 'counter_quote.ranges.plus_cost')
    const insertIndex =
      cols.findIndex((col) => col.field === 'counter_quote.ranges.plus_cost') +
      1;

    // Insert baseline columns if the feature flag is enabled
    if (flags?.baseline_cost?.enabled && insertIndex > 0) {
      // Create copies of the baseline columns with tooltips
      const baselineColumnsWithTooltips = baselineCostColumns.map((col) => ({
        ...col,
        // Add a tooltip to the header
        renderHeader: () => (
          <Tooltip
            position="top"
            arrow
            content={BASELINE_COST_TOOLTIP}
            zIndex={25}
          >
            <div className="flex gap-1 items-center my-3">
              {col.headerName}{' '}
              <Icon.InformationCircle className="text-neutral-400 text-sm flex-shrink-0" />
            </div>
          </Tooltip>
        ),
      }));

      // Insert the baseline columns at the correct position
      cols.splice(insertIndex, 0, ...baselineColumnsWithTooltips);
    }

    return cols;
  }, [flags?.baseline_cost?.enabled]);

  useEffect(() => {
    if (data?.length > 0) {
      setAllCounters(data);
      setSelectedCounter((prev: Counter | null) => {
        if (!prev) return prev;
        const updatedCounter = data.find(
          (row: Counter) => row.counter_id === prev.counter_id,
        );
        return updatedCounter || prev;
      });
    }
    if (vendorId && userId) {
      setVendorId(vendorId);
      setUserId(userId);
    }
  }, [data, setAllCounters, setSelectedCounter, vendorId, userId]);

  return (
    <>
      <CounterShippedDialog
        open={dialogState.counterShipped}
        handleClose={() => closeDialog('counterShipped')}
      />
      <PrevSampleReceivedDialog
        open={dialogState.prevSampleReceived}
        handleClose={() => closeDialog('prevSampleReceived')}
      />
      <CounterContactDialog
        open={dialogState.counterContacts}
        handleClose={() => closeDialog('counterContacts')}
      />
      <Lightbox
        slides={selectedPhotos}
        isOpen={isLightboxOpened}
        onClose={() => setIsLightboxOpened(false)}
      />
      <div
        className={`transition-all duration-300 ease-in-out h-full ${
          isDrawerOpen.opened ? 'w-[calc(100%-600px)]' : 'w-full'
        }`}
      >
        <DataGrid
          apiRef={apiRef}
          hideFilterButton
          hideGroupButton
          rows={transformedRows ?? []}
          paginationMode="server"
          sortingMode="server"
          loading={isLoading}
          initialState={{
            pinnedColumns: {
              left: ['photo', 'last_message.updated_at'],
            },
          }}
          columns={columns}
          {...others}
        />
      </div>
      <CounterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen({ opened: false, tab: 'chat' })}
      />
    </>
  );
}

export default CounterDatagrid;
